import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 500,
  },
  chipInput: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
}));

const EXPERTISE_LEVELS = ['beginner', 'intermediate', 'advanced', 'expert'];
const DOCUMENT_PURPOSES = ['reference', 'tutorial', 'regulation', 'methodology', 'best_practices', 'case_study'];
const COMMON_CONTEXTS = [
  'valuation',
  'succession_planning',
  'compliance',
  'marketing',
  'business_continuity',
  'deal_negotiation',
  'client_acquisition',
  'financial_planning',
];

const VectorDocumentForm = ({ open, onClose, document, onSubmit, categories }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    url: '',
    category: '',
    tags: [],
    applicableContexts: [],
    industryTags: [],
    expertiseLevel: 'intermediate',
    documentPurpose: 'reference',
    public: true, // Always public by default
    companyWide: false,
    allowedRoles: [],
    expiresAt: null,
    verified: false,
    citable: true, // Whether agents can cite this document
  });
  const [tagInput, setTagInput] = useState('');
  const [industryTagInput, setIndustryTagInput] = useState('');

  useEffect(() => {
    if (document) {
      setFormData({
        title: document.title || '',
        body: document.body || '',
        url: document.url || '',
        category: document.category || '',
        tags: document.tags || [],
        applicableContexts: document.applicableContexts || [],
        industryTags: document.industryTags || [],
        expertiseLevel: document.expertiseLevel || 'intermediate',
        documentPurpose: document.documentPurpose || 'reference',
        public: document.public || false,
        companyWide: document.companyWide || false,
        allowedRoles: document.allowedRoles || [],
        expiresAt: document.expiresAt,
        verified: document.verified || false,
        citable: document.citable !== false, // Default to true if not set
      });
    } else {
      // Reset form for new document
      setFormData({
        title: '',
        body: '',
        url: '',
        category: '',
        tags: [],
        applicableContexts: [],
        industryTags: [],
        expertiseLevel: 'intermediate',
        documentPurpose: 'reference',
        public: true, // Always public by default
        companyWide: false,
        allowedRoles: [],
        expiresAt: null,
        verified: false,
        citable: true, // Default to citable
      });
    }
  }, [document]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [field]: value });
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      setFormData({
        ...formData,
        tags: [...new Set([...formData.tags, tagInput.trim()])],
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleAddIndustryTag = (e) => {
    if (e.key === 'Enter' && industryTagInput.trim()) {
      e.preventDefault();
      setFormData({
        ...formData,
        industryTags: [...new Set([...formData.industryTags, industryTagInput.trim()])],
      });
      setIndustryTagInput('');
    }
  };

  const handleRemoveIndustryTag = (tagToRemove) => {
    setFormData({
      ...formData,
      industryTags: formData.industryTags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Clean up empty values
    const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
      if (value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        acc[key] = value;
      }
      return acc;
    }, {});
    onSubmit(cleanedData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{document ? 'Edit Document' : 'Create New Document'}</DialogTitle>
        <DialogContent>
          <Box className={classes.form}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={handleChange('title')}
              required
              fullWidth
            />

            <TextField
              label="Content"
              value={formData.body}
              onChange={handleChange('body')}
              multiline
              rows={6}
              fullWidth
              helperText="The main content that will be indexed for search"
            />

            <TextField
              label="URL"
              value={formData.url}
              onChange={handleChange('url')}
              fullWidth
              helperText="Optional: URL for PDF or external content"
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select value={formData.category} onChange={handleChange('category')}>
                    <MenuItem value="">None</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Document Purpose</InputLabel>
                  <Select
                    value={formData.documentPurpose}
                    onChange={handleChange('documentPurpose')}
                  >
                    {DOCUMENT_PURPOSES.map((purpose) => (
                      <MenuItem key={purpose} value={purpose}>
                        {purpose.replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Expertise Level</InputLabel>
              <Select value={formData.expertiseLevel} onChange={handleChange('expertiseLevel')}>
                {EXPERTISE_LEVELS.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <TextField
                label="Add Tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleAddTag}
                fullWidth
                helperText="Press Enter to add tags"
              />
              <Box className={classes.chipInput} mt={1}>
                {formData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    size="small"
                  />
                ))}
              </Box>
            </Box>

            <Autocomplete
              multiple
              options={COMMON_CONTEXTS}
              value={formData.applicableContexts}
              onChange={(e, value) => setFormData({ ...formData, applicableContexts: value })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Applicable Contexts"
                  helperText="Select contexts where this document is relevant"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} />
                ))
              }
            />

            <Box>
              <TextField
                label="Add Industry Tags"
                value={industryTagInput}
                onChange={(e) => setIndustryTagInput(e.target.value)}
                onKeyPress={handleAddIndustryTag}
                fullWidth
                helperText="e.g., financial_services, wealth_management"
              />
              <Box className={classes.chipInput} mt={1}>
                {formData.industryTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveIndustryTag(tag)}
                    size="small"
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Citation Settings
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox checked={formData.citable} onChange={handleChange('citable')} />
                }
                label="Citable"
              />
              <Typography variant="caption" color="textSecondary" display="block">
                When enabled, agents can cite this document with its URL in responses. When disabled, the document is still used for context but won't be cited.
              </Typography>
            </Box>

            {document && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Document Status
                </Typography>
                <Box display="flex" gap={2}>
                  <FormControlLabel
                    control={
                      <Checkbox checked={formData.verified} onChange={handleChange('verified')} />
                    }
                    label="Verified"
                  />
                </Box>
              </>
            )}

            {/* Temporarily disabled DateTimePicker - will add back later */}
            {/* <TextField
              label="Expires At"
              type="datetime-local"
              value={formData.expiresAt || ''}
              onChange={handleChange('expiresAt')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Optional: When this document should no longer be used"
            /> */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" color="primary" variant="contained">
            {document ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default VectorDocumentForm;
